import { ComissaoAssistenteResponse, ComissaoLocalReuniaoResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { OpcaoFiltroComissao } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';

/**
 * Funções puras de normalização e mapeamento de opções do dashboard de comissões.
 * Extraídas do componente (SRP) para orquestração enxuta e teste unitário direto.
 */

/** Número finito ou 0. */
export function numeroSeguro(valor?: number | null): number {
    return typeof valor === 'number' && Number.isFinite(valor) ? valor : 0;
}

/** Inteiro truncado e nunca negativo (0 quando inválido). */
export function normalizarInteiroNaoNegativo(valor?: number | null): number {
    if (typeof valor !== 'number' || !Number.isFinite(valor)) {
        return 0;
    }
    return Math.max(0, Math.trunc(valor));
}

/** Linhas por página válidas (> 0), com fallback de 20. */
export function normalizarLinhasPorPagina(valor?: number | null): number {
    const linhas = normalizarInteiroNaoNegativo(valor);
    return linhas > 0 ? linhas : 20;
}

/** Texto aparado e em maiúsculas. */
export function normalizarTexto(valor?: string | null): string {
    return (valor ?? '').trim().toUpperCase();
}

/** UF aparada e em maiúsculas. */
export function normalizarUf(valor?: string | null): string {
    return (valor ?? '').trim().toUpperCase();
}

/** Inteiro positivo a partir de string, ou null. */
export function parseNumeroPositivo(valor?: string | null): number | null {
    if (!valor) {
        return null;
    }
    const numero = Number(valor);
    if (!Number.isFinite(numero) || numero <= 0) {
        return null;
    }
    return Math.trunc(numero);
}

/** Opções de local (dedup por id, ordenadas), com a opção "Todos" no topo. */
export function mapearOpcoesLocais(locais: ComissaoLocalReuniaoResponse[]): OpcaoFiltroComissao[] {
    const mapa = new Map<number, string>();

    for (const local of locais) {
        const id = numeroSeguro(local.idLocalReuniao);
        const descricao = (local.dsLocalReuniao ?? '').trim();

        if (id > 0 && descricao) {
            mapa.set(id, descricao);
        }
    }

    const opcoes = Array.from(mapa.entries())
        .map(([id, rotulo]) => ({ valor: `id:${id}`, rotulo }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo));

    return [{ rotulo: 'Todos', valor: 'todos' }, ...opcoes];
}

/** Opções de assistente (descarta inválidos/sentinela "TODOS"), com "Todos" no topo. */
export function mapearOpcoesAssistentes(assistentes: ComissaoAssistenteResponse[]): OpcaoFiltroComissao[] {
    const mapa = new Map<string, string>();

    for (const assistente of assistentes) {
        const id = numeroSeguro(assistente.idSolicitante);
        const uf = normalizarTexto(assistente.ufSgUf);
        const nome = (assistente.nmSolicitante ?? '').trim();

        if (id <= 0 || uf.length !== 2 || !nome || nome.toUpperCase() === 'TODOS') {
            continue;
        }

        mapa.set(`id:${id}|uf:${uf}`, nome);
    }

    const opcoes = Array.from(mapa.entries())
        .map(([valor, rotulo]) => ({ valor, rotulo }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo));

    return [{ rotulo: 'TODOS', valor: 'todos' }, ...opcoes];
}
